import { ComponentType } from 'react'
import { TOnboardingStep } from '@forwardflow/types/onboarding'
import {
   PlanSelection,
   FubConnect,
   PaymentDetails,
   TeamInvitation,
   LeadsAvailability,
   UserAvailability,
   CnameSetup,
   PresentationStep,
   SummaryBeforeStaging,
   StageMappingNurtureLeads,
   StageMappingAppointmentStages,
   StageMappingStopStages,
   StageMappingDefinition,
   CampaignConfiguration,
   VoiceAssistant,
   SetupInBoxApp,
   DnDStageMappingOnboarding,
   PhoneNumberConfigurationV2,
   PricingStepV3,
} from './index'
import { CalendarConnection } from './steps/CalendarConnection'
import { GettingStarted } from './steps/GettingStarted'
import { StageMappingExplained } from './steps/StageMappingExplained'
import { YouAndTeam } from './steps/YouAndTeam'
import { BudgetAndCard } from './steps/BudgetAndCard'
import { NumberAndStops } from './steps/NumberAndStops'
import { FirstCampaign } from './steps/FirstCampaign'
import { Voice } from './steps/Voice'
import { StartCalling } from './steps/StartCalling'
import { Budget } from './steps/Budget'
import { Payment } from './steps/Payment'
import { AreaCode } from './steps/AreaCode'
import { YourNumber } from './steps/YourNumber'
import { StopRules } from './steps/StopRules'
import { BusinessDetailsStep } from './steps/BusinessDetailsStep'

/**
 * Step Registry Pattern
 *
 * Centralized mapping of onboarding steps to their respective components.
 * This pattern provides several benefits:
 *
 * 1. **Scalability**: Easy to add/remove/reorder steps
 * 2. **Maintainability**: All step mappings in one place
 * 3. **Type Safety**: TypeScript ensures all steps are mapped
 * 4. **Discoverability**: Clear overview of all available steps
 * 5. **Testability**: Can mock individual steps easily
 *
 * @example
 * ```tsx
 * const StepComponent = STEP_REGISTRY[currentStep]
 * return StepComponent ? <StepComponent /> : null
 * ```
 */
export const STEP_REGISTRY: Record<TOnboardingStep, ComponentType> = {
   'plan-selection': PricingStepV3,
   'fub-connect': FubConnect,
   'payment-details': PaymentDetails,
   'team-invitation': TeamInvitation,
   'calendar-connection': CalendarConnection,
   'leads-availability': LeadsAvailability,
   'user-availability': UserAvailability,
   'business-details': BusinessDetailsStep,
   'assistant-name': PlanSelection, // TODO: Create AssistantName component
   'setup-cname': CnameSetup,
   'presentation-step': PresentationStep,
   'summary-before-stage-mapping': SummaryBeforeStaging,
   'stage-mapping-new-leads': StageMappingNurtureLeads, // TODO: Create separate component if needed
   'stage-mapping-nurture-stages': StageMappingNurtureLeads,
   'stage-mapping-appointment': StageMappingAppointmentStages,
   'stage-mapping-stop-stages': StageMappingStopStages,
   'stage-mapping-definition': StageMappingDefinition,
   'campaign-configuration': CampaignConfiguration,
   'voice-agent-configuration': VoiceAssistant,
   'phone-number-configuration': PhoneNumberConfigurationV2,
   'install-inbox-app': SetupInBoxApp,
   'dnd-stage-mapping': DnDStageMappingOnboarding,
   'getting-started': GettingStarted,
   'stage-mapping-explained': StageMappingExplained,
   // Sub-3-minute flow (2026-10-14)
   'you-and-team': YouAndTeam,
   'budget-and-card': BudgetAndCard,
   'number-and-stops': NumberAndStops,
   'first-campaign': FirstCampaign,
   voice: Voice,
   'start-calling': StartCalling,
   // Seven-step setup (2026-10-14, later)
   budget: Budget,
   payment: Payment,
   'area-code': AreaCode,
   'your-number': YourNumber,
   'stop-rules': StopRules,
}

/**
 * Helper function to get a step component by step name.
 * Provides null-safety and easier testing.
 *
 * @param step - The onboarding step name
 * @returns The component for the given step, or null if not found
 */
export const getStepComponent = (
   step: TOnboardingStep
): ComponentType | null => {
   return Object.prototype.hasOwnProperty.call(STEP_REGISTRY, step)
      ? STEP_REGISTRY[step]
      : null
}
